import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Skeleton } from '@/components/Skeleton';
import { formatGstDateTime } from '@/lib/format';

import { useAdminDashboard } from './AdminPage.data';

// 6 KPIs per Spec §M9 L1289, set remappé ADR-028 §D3-D4 :
//   1. publishedDeals      — deals.status='published'
//   2. draftDeals          — deals.status='draft' (replaces "Awaiting validation")
//   3. pendingRequests     — buyer_requests.status IN ('new','in_progress')
//   4. pendingSubmissions  — seller_submissions.status IN ('new','in_progress')
//   5. registeredBuyers    — users.role='buyer'
//   6. soldDeals30d        — deals.status='sold' AND sold_at >= NOW()-30d
const METRIC_KEYS = [
  'admin.kpi.metrics.publishedDeals',
  'admin.kpi.metrics.draftDeals',
  'admin.kpi.metrics.pendingRequests',
  'admin.kpi.metrics.pendingSubmissions',
  'admin.kpi.metrics.registeredBuyers',
  'admin.kpi.metrics.soldDeals30d',
];

function formatActivityMessage(action: string, target_type: string | null): string {
  const target = target_type ? ` · ${target_type}` : '';
  return `${action}${target}`;
}

export function AdminPage() {
  const { t } = useTranslation();
  const { status, metrics, topDeals, activity } = useAdminDashboard();

  const metricValues = metrics
    ? [
        metrics.deals.published,
        metrics.deals.draft,
        metrics.requestsPending,
        metrics.submissionsPending,
        metrics.users.buyers,
        metrics.deals.sold30d,
      ]
    : [0, 0, 0, 0, 0, 0];

  const isLoading = status === 'loading';
  const isError = status === 'error';

  return (
    <>
      <header className="admin-head">
        <div>
          <h1 className="admin-head__title">{t('admin.kpi.title')}</h1>
          <div className="admin-head__sub">{t('admin.kpi.sub')}</div>
        </div>
      </header>

      {isError && (
        <div
          role="alert"
          style={{
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            background: 'var(--c-danger-bg, #fef2f2)',
            color: 'var(--c-danger, #dc2626)',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
          }}
        >
          Failed to load dashboard data. Please refresh the page.
        </div>
      )}

      <div className="admin-metric-grid">
        {METRIC_KEYS.map((key, i) => (
          <div key={key} className="admin-metric">
            <div className="admin-metric__label">{t(key)}</div>
            <div className="admin-metric__value">
              {isLoading ? <Skeleton width={48} height={28} /> : metricValues[i]}
            </div>
          </div>
        ))}
      </div>

      <div className="admin-section-grid">
        <section>
          <div className="admin-section__label">{t('admin.kpi.topDeals')}</div>
          <div className="admin-card">
            {isLoading
              ? Array.from({ length: 5 }, (_, i) => (
                  <Skeleton key={i} height={40} style={{ marginBottom: 8 }} />
                ))
              : topDeals.map((deal, idx) => (
                  <Link key={deal.id} to="/admin/deals" className="admin-top-row">
                    <span className="admin-top-row__rank">{String(idx + 1).padStart(2, '0')}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="admin-top-row__title">{deal.title}</div>
                      <div className="admin-top-row__meta">
                        <span className="admin-top-row__views">
                          {deal.reference_id ?? deal.id.slice(0, 8)}
                        </span>
                        <span className="admin-top-row__reqs">
                          {t('admin.kpi.topDealRequests', { count: deal.requests_count_7d })}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
          </div>
        </section>

        <section>
          <div className="admin-section__label">
            {t('admin.kpi.activity')}
            <span className="admin-section__live">{t('admin.kpi.live')}</span>
          </div>
          <div className="admin-card">
            {isLoading
              ? Array.from({ length: 6 }, (_, i) => (
                  <Skeleton key={i} height={28} style={{ marginBottom: 8 }} />
                ))
              : activity.map((row) => (
                  <div key={row.id} className="admin-activity">
                    <span className="admin-activity__time">
                      {formatGstDateTime(new Date(row.created_at))}
                    </span>
                    <span className="admin-activity__msg">
                      {formatActivityMessage(row.action, row.target_type)}
                    </span>
                  </div>
                ))}
          </div>
        </section>
      </div>
    </>
  );
}
